import { Printer, Search } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Button, EmptyState, Input, PageHeader, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, cn } from '@buildbee/ui';
import { GUIDE, type GuideBlock, type GuideSection, type GuideTopic } from './guide-content';

/** `**bold**` spans in guide text (names of buttons, fields and tabs). */
function Rich({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return <>{parts.map((part, i) => (i % 2 ? <strong key={i} className="font-semibold text-foreground">{part}</strong> : part))}</>;
}

function Block({ block }: { block: GuideBlock }) {
  switch (block.kind) {
    case 'p':
      return <p className="leading-relaxed text-muted-foreground"><Rich text={block.text} /></p>;
    case 'steps':
      return (
        <ol className="list-decimal space-y-1.5 pl-5 leading-relaxed text-muted-foreground marker:font-semibold marker:text-foreground">
          {block.items.map((item, i) => <li key={i} className="pl-1"><Rich text={item} /></li>)}
        </ol>
      );
    case 'bullets':
      return (
        <ul className="list-disc space-y-1.5 pl-5 leading-relaxed text-muted-foreground">
          {block.items.map((item, i) => <li key={i} className="pl-1"><Rich text={item} /></li>)}
        </ul>
      );
    case 'table':
      return (
        <div className="overflow-hidden rounded-lg bg-foreground/[0.03]">
          <Table>
            <TableHeader>
              <TableRow>{block.head.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {block.rows.map((row, r) => (
                <TableRow key={r}>
                  {row.map((cell, c) => (
                    <TableCell key={c} className={cn('align-top', c === 0 ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                      <Rich text={cell} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
  }
}

const blockText = (b: GuideBlock): string => (b.kind === 'p' ? b.text : b.kind === 'table' ? [...b.head, ...b.rows.flat()].join(' ') : b.items.join(' '));
const topicMatches = (t: GuideTopic, q: string) => `${t.title} ${t.blocks.map(blockText).join(' ')}`.toLowerCase().includes(q);

/** The sections and topics that mention every word of `query` (all of them when it is empty). */
function filterGuide(query: string): GuideSection[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [...GUIDE];
  return GUIDE.flatMap((s) => {
    const topics = s.topics.filter((t) => words.every((w) => topicMatches(t, w) || s.title.toLowerCase().includes(w)));
    return topics.length ? [{ ...s, topics }] : [];
  });
}

/** In-page link: scrolls instead of changing the route's hash. */
function Jump({ id, className, children }: { id: string; className?: string; children: ReactNode }) {
  return (
    <a
      href={`#${id}`}
      className={className}
      onClick={(e) => {
        e.preventDefault();
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }}
    >
      {children}
    </a>
  );
}

/** The BuildBee user guide and cheat sheet, for every signed-in user (/help). */
export function HelpPage() {
  const [query, setQuery] = useState('');
  const sections = useMemo(() => filterGuide(query), [query]);

  return (
    <div>
      <PageHeader
        title="User guide"
        description="Step-by-step help for every part of BuildBee, a cheat sheet, and how cost codes tie the numbers together."
        actions={
          <Button variant="outline" className="print:hidden" onClick={() => window.print()}>
            <Printer /> Print
          </Button>
        }
      />
      <div className="grid gap-8 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <aside className="print:hidden lg:sticky lg:top-24 lg:self-start">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input type="search" aria-label="Search the guide" placeholder="Search the guide" className="pl-9" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <nav aria-label="Guide contents" className="mt-4 hidden lg:block">
            <ul className="space-y-0.5 text-sm">
              {sections.map((s) => (
                <li key={s.id}>
                  <Jump id={s.id} className="block rounded-md px-2 py-1.5 font-medium text-foreground hover:bg-foreground/[0.04]">
                    {s.title}
                  </Jump>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <div className="min-w-0 space-y-6">
          {sections.length === 0 ? (
            <EmptyState title="Nothing matches" description={`No part of the guide mentions “${query.trim()}”. Try another word.`} action={<Button variant="outline" onClick={() => setQuery('')}>Clear search</Button>} />
          ) : null}
          {sections.map((s) => (
            <section key={s.id} id={s.id} aria-labelledby={`${s.id}-title`} className="scroll-mt-24 rounded-xl bg-card p-6 shadow-card print:break-inside-avoid-page print:shadow-none sm:p-8">
              <h2 id={`${s.id}-title`} className="text-2xl font-semibold tracking-[-0.02em]">{s.title}</h2>
              <p className="mt-2 max-w-3xl leading-relaxed text-muted-foreground">{s.lead}</p>
              <div className="mt-6 space-y-8">
                {s.topics.map((t) => (
                  <div key={t.id} id={`${s.id}-${t.id}`} className="scroll-mt-24 space-y-3">
                    <h3 className="text-[17px] font-semibold tracking-[-0.01em]">{t.title}</h3>
                    {t.blocks.map((b, i) => <Block key={i} block={b} />)}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
